import React, { useState, useEffect, useRef } from 'react'
import {
    Box,
    Typography,
    Paper,
    Container,
    Avatar,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    IconButton,
    Button
} from '@mui/material'
import {
    Face as FaceIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useFaceIO } from '../../context/FaceIOContext.jsx'
import { attendanceApi } from '../../api/attendanceApi'
import { getTimestampISO } from '../../utils/formatters'
import toast from 'react-hot-toast'

const Kiosk = () => {
    const navigate = useNavigate()
    const { faceioInstance, error: faceIoError } = useFaceIO()
    const [deviceInfo, setDeviceInfo] = useState(null)
    const [scanStatus, setScanStatus] = useState('idle') // idle, scan, success, error
    const [lastUser, setLastUser] = useState(null)
    const [message, setMessage] = useState('Ready to Scan')

    // Ref to ongoing scan to prevent double triggers
    const isScanning = useRef(false)

    const handleLogout = () => {
        if (window.confirm("Exit Kiosk Mode?")) {
            // Stop scanning immediately
            isScanning.current = false
            setScanStatus('idle')
            setMessage("Logging out...")

            // Attempt to clear FaceIO overlay
            try {
                faceioInstance?.restartSession()
            } catch (e) {
                console.warn("FaceIO restart failed during logout", e)
            }

            // Delay redirect to allow overlay to clear and user to see feedback
            setTimeout(() => {
                localStorage.removeItem('device_token')
                localStorage.removeItem('device_info')
                // Force reload to clear FaceIO instance completely
                window.location.href = '/device/login'
            }, 2000)
        }
    }

    useEffect(() => {
        // Check device authentication
        const token = localStorage.getItem('device_token')
        const deviceData = localStorage.getItem('device_info')

        if (!token) {
            navigate('/device/login')
            return
        }

        if (deviceData) {
            setDeviceInfo(JSON.parse(deviceData))
        }

        // Add Escape key listener for logout
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleLogout()
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            // Cleanup FaceIO overlay when leaving the component
            try {
                faceioInstance?.restartSession()
            } catch (e) {
                console.warn("FaceIO cleanup failed", e)
            }
        }
    }, [navigate, faceioInstance])

    // Start scanning automatically when component mounts or after reset
    useEffect(() => {
        let timeoutId

        if (faceioInstance && scanStatus === 'idle' && !isScanning.current) {
            startScanning()
        }

        // Auto-reset after success/error
        if (scanStatus === 'success' || scanStatus === 'error') {
            timeoutId = setTimeout(() => {
                setScanStatus('idle')
                setLastUser(null)
                setMessage('Ready to Scan')
                faceioInstance.restartSession()
            }, 4000) // 4 seconds delay
        }

        return () => clearTimeout(timeoutId)
    }, [faceioInstance, scanStatus])


    const startScanning = async () => {
        if (!faceioInstance || isScanning.current) return

        isScanning.current = true
        // Only set 'scan' status if we are not already in it, to avoid flickering
        if (scanStatus !== 'scan') {
            setScanStatus('scan')
            setMessage('Looking for face...')
        }

        try {
            const userData = await faceioInstance.authenticate({
                locale: "auto",
                permissionTimeout: 5,
                idleTimeout: 60, // Wait 60 seconds for a face before refreshing
                replyTimeout: 10, // Allow more time for processing
                termsTimeout: 5
            })

            console.log("Kiosk: Face Detected", userData)
            handleFaceDetected(userData)

        } catch (errCode) {
            // FaceIO Errors
            // We want to SILENTLY restart for common "nobody there" errors
            const silentErrors = [
                1, // NO_FACES_DETECTED (Start again)
                2, // UNRECOGNIZED_FACE (Maybe show a small toast but don't stop) - actually let's just retry
                3, // TIMEOUT (Nobody there)
                12 // TIMEOUT again (faceio specific)
            ]

            console.log("Kiosk: Scan end code:", errCode)

            if (silentErrors.includes(errCode)) {
                // Just restart immediately without showing error screen
                console.log("Silent restart...")
                isScanning.current = false
                // Small delay to prevent CPU thrashing if it fails instantly
                setTimeout(() => {
                    if (scanStatus !== 'scan') {
                        setScanStatus('scan')
                        setMessage('Looking for face...')
                    }
                    // Restart logic:
                    // We must call startScanning() again to create a new Promise.
                    // restartSession() alone might not bridge to our async/await flow.
                    startScanning()
                }, 500)
            } else {
                // Actual errors (Permission denied, etc)
                setScanStatus('error')
                handleError(errCode)
                isScanning.current = false
                // Auto-retry even on error after delay
                setTimeout(() => {
                    faceioInstance?.restartSession()
                }, 5000)
            }
        }
    }

    const handleFaceDetected = async (userData) => {
        setScanStatus('processing')
        setMessage('Processing...')

        try {
            const employeeId = userData.payload.id
            const facialId = userData.facialId

            // FIX: Get device info directly from localStorage to avoid stale state in closure
            // immediately after login/mount.
            const storedDeviceData = localStorage.getItem('device_info')
            const currentDevice = storedDeviceData ? JSON.parse(storedDeviceData) : deviceInfo

            const now = new Date()

            let punchType = 'check-in'
            let userName = userData.payload.name

            try {
                // Use raw fetch (not axios) to avoid the 401/404 interceptor showing
                // spurious "Employee not found" toasts and defaulting to check-in.
                const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://facedevapi.intellisiatechnologies.com'
                const deviceToken = localStorage.getItem('device_token')
                const todayRes = await fetch(`${baseUrl}/employee/attendance/today`, {
                    headers: {
                        'X-Employee-ID': String(employeeId),
                        'X-Org-ID': String(currentDevice?.org_id || ''),
                        ...(deviceToken && { 'Authorization': `Bearer ${deviceToken}` }),
                    },
                })

                if (todayRes.ok) {
                    const resData = await todayRes.json()
                    const data = resData.data || resData

                    if (data) {
                        const hasCheckIn = data.check_in && data.check_in !== '-'
                        const hasCheckOut = data.check_out && data.check_out !== '-'

                        if (hasCheckIn && hasCheckOut) {
                            // Already completed both for today — block further action
                            setScanStatus('error')
                            const msg = `Already checked out today at ${data.check_out}`
                            setMessage(msg)
                            toast.error(msg)
                            isScanning.current = false
                            return
                        } else if (hasCheckIn && !hasCheckOut) {
                            punchType = 'check-out'
                        } else {
                            punchType = 'check-in'
                        }
                    }
                } else if (todayRes.status === 404) {
                    // No record yet today → first punch is check-in
                    console.log('No attendance record for today, defaulting to check-in')
                    punchType = 'check-in'
                } else {
                    console.warn('Unexpected status fetching today attendance:', todayRes.status)
                }
            } catch (e) {
                console.warn('Could not fetch today attendance, defaulting to check-in', e)
            }

            const payload = {
                employee_id: employeeId,
                punch_type: punchType,
                timestamp: getTimestampISO(),
                facial_id: facialId,
                device_id: currentDevice?.id,
                location: currentDevice?.location || 'Kiosk'
            }

            console.log("Marking Attendance:", payload)

            await attendanceApi.markAttendance(payload)

            setLastUser({
                name: userName,
                type: punchType,
                time: now.toLocaleTimeString()
            })
            setScanStatus('success')
            setMessage(punchType === 'check-in' ? `Welcome, ${userName}` : `Goodbye, ${userName}`)
            toast.success(`${punchType === 'check-in' ? 'Check-in' : 'Check-out'} Successful`)

        } catch (error) {
            console.error("Attendance Mark Failed", error)
            setScanStatus('error')
            const msg = error.response?.data?.detail || 'Failed to mark attendance'
            setMessage(msg)
            toast.error(msg)
        } finally {
            isScanning.current = false
        }
    }

    const handleError = (errCode) => {
        let msg = "Scan failed"
        // ... (Same error switch as TestFaceAttendance)
        // For brevity, using simplified mapped errors
        if (errCode === 11) msg = "Session Expired"
        if (errCode === 2) msg = "No face detected"

        setMessage(msg)
    }



    return (
        <Box sx={{
            height: '100vh',
            bgcolor: '#000',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Top Bar */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#111' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
                        {deviceInfo?.name || 'Please Login'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'gray' }}>
                        {deviceInfo?.location || 'Kiosk Termial'}
                    </Typography>
                </Box>
                <Box>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogout}
                        sx={{ borderColor: '#333', color: '#ff5252' }}
                    >
                        Exit Kiosk
                    </Button>
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative'
            }}>

                {/* Status Circle */}
                <Box sx={{
                    position: 'relative',
                    width: 300,
                    height: 300,
                    borderRadius: '50%',
                    border: '4px solid',
                    borderColor:
                        scanStatus === 'success' ? '#4caf50' :
                            scanStatus === 'error' ? '#f44336' :
                                scanStatus === 'scan' ? '#2196f3' : '#333',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    animation: scanStatus === 'scan' ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                        '0%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.4)' },
                        '70%': { boxShadow: '0 0 0 20px rgba(33, 150, 243, 0)' },
                        '100%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)' }
                    }
                }}>

                    {scanStatus === 'success' ? (
                        <CheckCircleIcon sx={{ fontSize: 100, color: '#4caf50' }} />
                    ) : scanStatus === 'error' ? (
                        <WarningIcon sx={{ fontSize: 100, color: '#f44336' }} />
                    ) : (
                        <FaceIcon sx={{ fontSize: 100, color: scanStatus === 'scan' ? '#2196f3' : '#666' }} />
                    )}

                    <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold', textAlign: 'center', px: 3, wordBreak: 'break-word', lineHeight: 1.3 }}>
                        {message}
                    </Typography>
                </Box>

            </Box>

            {/* Bottom Info Status */}
            {lastUser && scanStatus === 'success' && (
                <Box sx={{
                    p: 4,
                    bgcolor: '#1a1a1a',
                    borderTop: '1px solid #333',
                    textAlign: 'center',
                    animation: 'slideUp 0.5s ease-out',
                    '@keyframes slideUp': {
                        from: { transform: 'translateY(100%)' },
                        to: { transform: 'translateY(0)' }
                    }
                }}>
                    <Avatar sx={{ width: 80, height: 80, margin: '0 auto', mb: 2, bgcolor: 'primary.main', fontSize: 32 }}>
                        {lastUser.name.charAt(0)}
                    </Avatar>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {lastUser.name}
                    </Typography>
                    <Chip
                        label={`${lastUser.type === 'check-in' ? 'CHECKED IN' : 'CHECKED OUT'} • ${lastUser.time}`}
                        color={lastUser.type === 'check-in' ? 'success' : 'warning'}
                        sx={{ fontSize: '1.2rem', py: 2.5, px: 2 }}
                    />
                </Box>
            )}

            {/* Instructions Footer */}
            {scanStatus === 'idle' && (
                <Box sx={{ p: 3, textAlign: 'center', color: '#666' }}>
                    <Typography>Please stand in front of the camera to mark attendance</Typography>
                </Box>
            )}

        </Box>
    )
}

export default Kiosk
